// Inspired by MUI's `usePagination` hook:
// https://github.com/mui/material-ui/blob/master/packages/mui-material/src/usePagination/usePagination.js
import { array } from "fp-ts"

export interface UsePaginationProps {
  /**
   * Number of always visible pages at the beginning and end.
   * @default 1
   */
  boundaryCount?: number
  /**
   * The total number of pages.
   * @default 1
   */
  totalPages: number
  /**
   * The current page.
   */
  page: number
  /**
   * Number of always visible pages before and after the current page.
   * @default 1
   */
  siblingCount?: number
}

export type PaginationItem =
  | {
      type: "ellipsis"
      position: "start" | "end"
    }
  | {
      type: "page"
      number: number
      active: boolean
    }

export interface UsePaginationResult {
  items: Array<PaginationItem>
  hasPrevious: boolean
  hasNext: boolean
}

const range = (start: number, end: number) => {
  const length = end - start + 1
  return Array.from({ length }, (_, i) => start + i)
}

export function usePagination(props: UsePaginationProps): UsePaginationResult {
  // keep default values in sync with @default tags in Pagination.propTypes
  const {
    boundaryCount = 1,
    totalPages = 1,
    page = 1,
    siblingCount = 1,
  } = props

  const startPages = range(1, Math.min(boundaryCount, totalPages))
  const endPages = range(
    Math.max(totalPages - boundaryCount + 1, boundaryCount + 1),
    totalPages
  )

  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      page - siblingCount,
      // Lower boundary when page is high
      totalPages - boundaryCount - siblingCount * 2 - 1
    ),
    // Greater than startPages
    boundaryCount + 2
  )

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      page + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2
    ),
    // Less than endPages
    array.isNonEmpty(endPages) ? endPages[0] - 2 : totalPages - 1
  )

  const itemList: Array<number | "ellipsis-start" | "ellipsis-end"> = [
    ...startPages,

    ...(siblingsStart > boundaryCount + 2
      ? ["ellipsis-start" as const]
      : boundaryCount + 1 < totalPages - boundaryCount
      ? [boundaryCount + 1]
      : []),

    ...range(siblingsStart, siblingsEnd),

    ...(siblingsEnd < totalPages - boundaryCount - 1
      ? ["ellipsis-end" as const]
      : totalPages - boundaryCount > boundaryCount
      ? [totalPages - boundaryCount]
      : []),

    ...endPages,
  ]

  // Convert the basic item list to PaginationItem props objects
  const items: Array<PaginationItem> = itemList.map((item) => {
    if (item === "ellipsis-start") {
      return {
        type: "ellipsis",
        position: "start",
      }
    }

    if (item === "ellipsis-end") {
      return {
        type: "ellipsis",
        position: "end",
      }
    }

    return {
      type: "page",
      number: item,
      active: item === page,
      "aria-current": item === page ? "true" : undefined,
    }
  })

  const hasPrevious = page > 1
  const hasNext = page < totalPages

  return {
    items,
    hasPrevious,
    hasNext,
  }
}
